import React, { useEffect, useMemo, useState } from 'react';
import { Avatar, Box, CircularProgress, Dialog, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import GradeIcon from '../../../components/common/GradeIcon';
import defaultUserThumbnail from '../../../assets/default_user_thumbnail.svg';
import theme from '../../../theme';
import { usersApi } from '../../../api/users';
import { useUserStore } from '../../../stores/userStore';

const TABS = [
  { id: 'followers', label: '팔로워' },
  { id: 'following', label: '팔로잉' },
];

const getFollowButtonTone = (isFollowing) =>
  isFollowing
    ? {
        bg: 'var(--pp-surface)',
        fg: 'var(--pp-text-primary)',
        border: 'var(--pp-border)',
      }
    : {
        bg: 'var(--pp-button-primary-bg)',
        fg: 'var(--pp-button-primary-fg)',
        border: 'var(--pp-button-primary-bg)',
      };

const FollowListDialog = ({
  open,
  onClose,
  nickname,
  followersCount = 0,
  followingCount = 0,
  initialTab = 'followers',
  onProfileUserUpdate,
}) => {
  const navigate = useNavigate();
  const currentUser = useUserStore((state) => state.user);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);
  const [displayCounts, setDisplayCounts] = useState({
    followers: followersCount,
    following: followingCount,
  });
  const [loading, setLoading] = useState(false);
  const [followLoadingUserId, setFollowLoadingUserId] = useState(null);

  useEffect(() => {
    if (open) {
      setActiveTab(initialTab);
    }
  }, [initialTab, open]);

  useEffect(() => {
    if (!open || !nickname) return;

    const fetchFollowLists = async () => {
      setLoading(true);
      try {
        const [followersResponse, followingResponse] = await Promise.all([
          usersApi.getFollowers(nickname),
          usersApi.getFollowing(nickname),
        ]);
        setFollowers(followersResponse.data || []);
        setFollowing(followingResponse.data || []);
        setDisplayCounts({
          followers: (followersResponse.data || []).length,
          following: (followingResponse.data || []).length,
        });
      } catch (error) {
        console.error('팔로우 목록을 불러오지 못했습니다:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFollowLists();
  }, [nickname, open]);

  const activeUsers = activeTab === 'followers' ? followers : following;

  const tabCounts = useMemo(
    () => ({
      followers: displayCounts.followers,
      following: displayCounts.following,
    }),
    [displayCounts.followers, displayCounts.following],
  );

  const replaceUserInLists = (updatedUser) => {
    const replace = (items) => items.map((item) => (item.id === updatedUser.id ? updatedUser : item));
    setFollowers(replace);
    setFollowing(replace);
  };

  const handleUserClick = (userNickname) => {
    onClose();
    navigate(`/users/${encodeURIComponent(userNickname)}`);
  };

  const handleFollowToggle = async (targetUser) => {
    if (!targetUser?.id || followLoadingUserId) return;

    const wasFollowing = targetUser.isFollowing;
    const isOwnProfile = currentUser?.nickname === nickname;
    setFollowLoadingUserId(targetUser.id);
    try {
      const response = targetUser.isFollowing
        ? await usersApi.unfollowUser(targetUser.id)
        : await usersApi.followUser(targetUser.id);
      const updatedUser = response.data;

      if (isOwnProfile && wasFollowing) {
        replaceUserInLists(updatedUser);
        setDisplayCounts((prev) => ({
          ...prev,
          following: Math.max(0, Number(prev.following || followingCount) - 1),
        }));
        onProfileUserUpdate?.((prev) => ({
          ...(prev || {}),
          followingCount: Math.max(0, Number(prev?.followingCount || followingCount) - 1),
        }));
      } else if (isOwnProfile && !wasFollowing) {
        replaceUserInLists(updatedUser);
        setDisplayCounts((prev) => ({
          ...prev,
          following: Number(prev.following || 0) + 1,
        }));
        onProfileUserUpdate?.((prev) => ({
          ...(prev || {}),
          followingCount: Number(prev?.followingCount || followingCount) + 1,
        }));
      } else {
        replaceUserInLists(updatedUser);
      }

      if (updatedUser.nickname === nickname) {
        onProfileUserUpdate?.((prev) => ({ ...(prev || updatedUser), ...updatedUser }));
      }
    } catch (error) {
      console.error('팔로우 상태 변경 실패:', error);
    } finally {
      setFollowLoadingUserId(null);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      sx={{
        '& .MuiDialog-paper': {
          width: { xs: 'calc(100% - 32px)', md: 420 },
          maxHeight: { xs: '80vh', md: 560 },
          borderRadius: '6px',
          m: { xs: 2, md: 4 },
        },
      }}
    >
      <Box sx={{ px: 2.5, pt: 2.25, pb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography sx={{ fontSize: { xs: 18, md: 20 }, fontWeight: 700 }}>
            {nickname}
          </Typography>
          <IconButton onClick={onClose} size="small" aria-label="닫기">
            <CloseIcon sx={{ width: 22, height: 22 }} />
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex', gap: 2.5, mt: 2 }}>
          {TABS.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <Box
                key={tab.id}
                component="button"
                type="button"
                onClick={() => setActiveTab(tab.id)}
                sx={{
                  p: 0,
                  pb: 0.75,
                  border: 0,
                  borderBottom: '2px solid',
                  borderColor: isActive ? theme.palette.black[9] : 'transparent',
                  background: 'transparent',
                  color: isActive ? theme.palette.black[9] : theme.palette.black[3],
                  fontSize: { xs: 15, md: 16 },
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                {tab.label} {tabCounts[tab.id]}
              </Box>
            );
          })}
        </Box>
      </Box>

      <Box sx={{ borderTop: `1px solid ${theme.palette.grey.line}` }} />

      <Box sx={{ minHeight: 180, maxHeight: { xs: '58vh', md: 410 }, overflowY: 'auto' }}>
        {loading ? (
          <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={28} />
          </Box>
        ) : activeUsers.length === 0 ? (
          <Typography
            sx={{
              py: 6,
              textAlign: 'center',
              fontSize: 14,
              color: theme.palette.black[3],
            }}
          >
            {activeTab === 'followers' ? '팔로워가 없습니다.' : '팔로잉한 회원이 없습니다.'}
          </Typography>
        ) : (
          activeUsers.map((item) => {
            const isMe = currentUser?.id === item.id;
            return (
              <Box
                key={item.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  px: 2.5,
                  py: 1.5,
                  borderBottom: `1px solid ${theme.palette.grey.bg}`,
                }}
              >
                <Avatar
                  src={item.profileImageUrl || defaultUserThumbnail}
                  onClick={() => handleUserClick(item.nickname)}
                  sx={{
                    width: { xs: 40, md: 44 },
                    height: { xs: 40, md: 44 },
                    border: `1px solid ${theme.palette.grey.line}`,
                    cursor: 'pointer',
                  }}
                />
                <Box
                  onClick={() => handleUserClick(item.nickname)}
                  sx={{
                    minWidth: 0,
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    cursor: 'pointer',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: { xs: 14, md: 15 },
                      fontWeight: 700,
                      color: theme.palette.black[6],
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {item.nickname}
                  </Typography>
                  <GradeIcon grade={item.grade} size={16} />
                </Box>
                {!isMe && (
                  <Box
                    component="button"
                    type="button"
                    disabled={followLoadingUserId === item.id}
                    onClick={() => handleFollowToggle(item)}
                    sx={() => {
                      const followButtonTone = getFollowButtonTone(item.isFollowing);

                      return {
                        appearance: 'none',
                        boxSizing: 'border-box',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: { xs: 86, md: 92 },
                        height: 32,
                        px: 1,
                        py: 0,
                        m: 0,
                        borderRadius: '4px',
                        fontSize: 12,
                        fontWeight: 700,
                        lineHeight: 1,
                        boxShadow: 'none',
                        cursor: followLoadingUserId === item.id ? 'default' : 'pointer',
                        color: `${followButtonTone.fg} !important`,
                        WebkitTextFillColor: `${followButtonTone.fg} !important`,
                        backgroundColor: followButtonTone.bg,
                        border: `1px solid ${followButtonTone.border}`,
                        transition: 'none',
                        opacity: 1,
                        '&:hover, &:focus, &:active, &:disabled': {
                          backgroundColor: followButtonTone.bg,
                          border: `1px solid ${followButtonTone.border}`,
                          color: `${followButtonTone.fg} !important`,
                          WebkitTextFillColor: `${followButtonTone.fg} !important`,
                          outline: 'none',
                        },
                        '&, & *': {
                          color: `${followButtonTone.fg} !important`,
                          WebkitTextFillColor: `${followButtonTone.fg} !important`,
                        },
                      };
                    }}
                  >
                    {item.isFollowing ? '팔로우 중' : '팔로우'}
                  </Box>
                )}
              </Box>
            );
          })
        )}
      </Box>
    </Dialog>
  );
};

export default FollowListDialog;
